/*
 * Wmb, Worldline Merchant Billing.
 * Copyright (C) 2017-2019 Worldline
 * All Rights Reserved
 * This product is protected by copyright. Any copying or distribution
 * is forbidden without the agreement of Worldline.
 */
import { Injectable } from '@angular/core';

import { ConfigurationService } from 'app/core/services/configuration.service';
import { NGXLogger } from 'ngx-logger';
import { BehaviorSubject, Observable, Subscription, interval } from 'rxjs';
import { NotifMessageService } from 'app/core/services/notifmessage.service';

import Keycloak, { KeycloakLogoutOptions } from 'keycloak-js';

export const EVENTS = ['START', 'INITIALIZED', 'LOGGED_IN', 'LOGGED_OUT', 'TOKEN_REFRESHED', 'TOKEN_VALIDITY_CHECKED', 'TOKEN_EXPIRED', 'NOT_AUTHENTICATED'];

class WaitInitToken {

    terminated: boolean = false;

    finalResult: boolean;

    successCallback: any;

    errorCallback: any;

    setResult(result: boolean) {
        this.terminated = true;
        this.finalResult = result;
        if (this.finalResult && this.successCallback) {
            this.successCallback(true);
        }
        else if (!this.finalResult && this.errorCallback) {
            this.errorCallback("[KeyCloak] error while initializing !");
        }
    };

    promise(): Promise<boolean> {

        return new Promise<any>((resolve, reject) => {
            if (resolve) {
                if (this.terminated && this.finalResult) {
                    resolve(true);
                } else if (!this.terminated) {
                    this.successCallback = resolve;
                }
            }
            if (reject) {
                if (this.terminated && !this.finalResult) {
                    reject("[keyCloak] error in initialisation !");
                }
                else if (!this.terminated) {
                    this.errorCallback = reject;
                }
            }
        })
    };

}

@Injectable()
export class KeycloakService {

    private roles: string[] = [];
    private checkTimeOutSub: Subscription;
    private modalResponseSubscription: Subscription;

    private _userInfo: string[] = [];
    get userInfo() {
        return this._userInfo;
    }

    private keycloakAuth: Keycloak;

    private eventStream: BehaviorSubject<string> = new BehaviorSubject('START');

    private keycloakInitialization = {
        finished: false,
        success: false,
        promisesToNotify: Array<WaitInitToken>()
    }

    private rolesSubject: BehaviorSubject<string[]> = new BehaviorSubject(this.roles);
    private userInfoSubject: BehaviorSubject<string[]> = new BehaviorSubject(this._userInfo);

    constructor(
        private logger: NGXLogger,
        private configService: ConfigurationService,
        private notifService: NotifMessageService
    ) {

    }

    public initialize(): Promise<any> {
        if (this.configService.config.security.keycloakActive) {
            this.keycloakAuth = new Keycloak({
                "realm": this.configService.config.security.keycloak.realm,
                "url": this.configService.config.security.keycloak.authserver,
                "clientId": this.configService.config.security.keycloak.clientId
            });
            return this._initialize()
                .then(() => this.logger.info("[KeyCloak] Service Initialized !"))
                .catch((e) => this.logger.error("[KeyCloak] Error while initializing KeyCloak Service, Authentification will not be available !", e));
        }
        else {
            this.logger.warn("[KeyCloak] Not active ! see your environment config file ...");
            return Promise.resolve();
        }
    }

    // initialize keycloak with a check if already logged in 
    // notify all pending request for the keycloak service when done 
    private _initialize(): Promise<any> {
        // initialize and verify if we are already logged in !
        const p: Promise<boolean> = new Promise((resolve, reject) => {
            if (this.isKeyCloakActive()) {
                this.logger.debug("[KeyCloak] Call to KeyCloakService initialization ...")
                this.keycloakAuth.init({ onLoad: 'check-sso', checkLoginIframe: false, responseMode: "fragment" })
                    .then((isSuccess) => {
                        // Pour initialiser les champs nom d'utilisateur
                        this.getInfosUser().then();
                        // notify ...
                        this.eventStream.next('INITIALIZED');
                        if (this.keycloakAuth.authenticated) {
                            this.logger.info('[KeyCloak] Init end, Already logged in !');
                            this.checkTimeOut();
                            // notify ...
                            //this.eventStream.next('LOGGED_IN');
                        }
                        else {
                            this.logger.info('[KeyCloak] Init ended, not logged in !');
                            // notify ...
                            this.eventStream.next('LOGGED_OUT');
                        }
                        resolve(isSuccess);
                        // if some call are waiting for end of initialization,
                        // notify them that it has ended with success !
                        this.notifyEndOfInit(isSuccess);

                    })
                    .catch(() => {
                        reject();
                        // if some call are waiting for end of initialization,
                        // notify them that it has ended with failure !
                        this.notifyEndOfInit(false);
                    });
            }
            else {
                reject();
                // if some call are waiting for end of initialization,
                // notify them that it has ended with failure  !
                this.notifyEndOfInit(false);
            }
        });

        return p;
    }

    // notify all promise that wait for the end of the initialization !
    private notifyEndOfInit(success: boolean) {
        this.keycloakInitialization.success = success;
        this.keycloakInitialization.finished = true;
        while (this.keycloakInitialization.promisesToNotify.length) {
            let currentpromise: WaitInitToken = this.keycloakInitialization.promisesToNotify.pop();
            currentpromise.setResult(success);
        }
    }

    waitForInitializationEnd(): Promise<boolean> {
        // if initialization done, return a promise with the result immediately available
        if (this.keycloakInitialization.finished) {
            if (this.keycloakInitialization.success) {
                return Promise.resolve(true);
            }
            else {
                return Promise.reject("[KeyCloak] Error while initialise ...");
            }
        }
        else {
            let token = new WaitInitToken();

            // store that "super promise" in the waiting list of promise
            this.keycloakInitialization.promisesToNotify.push(token);
            // si entre le debut de cette methode et ici,
            // l'initilisation s'est terminee, on traite la liste en attente ....
            if (this.keycloakInitialization.finished) {
                this.notifyEndOfInit(this.keycloakInitialization.success);
            }
            // return the promise 
            return token.promise();
        }
    }

    // On Demand Login ...
    login(redirectUrlAfterLogin?: string): Observable<boolean> {
        this.logger.log("Call to login ...");
        return Observable.create(observer => {
            if (this.isKeyCloakActive()) {
                let options = undefined;
                if (this.configService.config.security.afterLoginRedirectURL != undefined) {
                    options = { redirectUri: this.configService.config.security.afterLoginRedirectURL };
                }
                if (redirectUrlAfterLogin != undefined) {
                    options = { redirectUri: redirectUrlAfterLogin };
                }
                this.keycloakAuth.login(options)
                    .then((isSuccess) => {
                        if (this.keycloakAuth.authenticated) {
                            this.logger.info('[KeyCloak] login success' + isSuccess);
                            // notify ...
                            //this.eventStream.next('LOGGED_IN');

                            observer.next(true);
                        }
                        else {
                            this.logger.info('[KeyCloak] login failed : ' + isSuccess);

                            // notify 
                            this.eventStream.next('LOGGED_OUT');

                            observer.next(false);
                        }
                        observer.complete();
                    })
                    .catch((result) => {
                        this.logger.error('[KeyCloak] errror while logging in : ' + result);
                        // in case of error, clear all token and authentification information
                        this.keycloakAuth.clearToken();

                        // notify 
                        this.eventStream.next('LOGGED_OUT');

                        // notify issue while logging in...
                        observer.next(false);
                        observer.complete();
                    });
            }
            else {
                this.logger.warn("[KeyCloak] Not Active ! Not Logging in !")
                observer.next(false);
                observer.complete();
            }
        });
    }

    // is the user logged in currently ? 
    // that function does not wait for a pending initialization ...
    isLoggedIn(): boolean {
        return this.isKeyCloakActive() && this.keycloakAuth.authenticated;
    }

    // is keycloak active in that application ?
    isKeyCloakActive(): boolean {
        if (this.keycloakAuth) {
            return true;
        }
        else {
            return false;
        }
    }

    // on demande Log Out ...
    logout(redirectUrlAfterLogout?: string): Observable<boolean> {
        this.logger.log("Call to log out ...");
        return new Observable(observer => {
            if (this.keycloakAuth) {
                let options: KeycloakLogoutOptions = undefined;
                if (this.configService.config.security.afterlogoutRedirectURL != undefined) {
                    options = { redirectUri: this.configService.config.security.afterlogoutRedirectURL };
                }
                if (redirectUrlAfterLogout != undefined) {
                    options = { redirectUri: redirectUrlAfterLogout };
                }
                this.keycloakAuth.logout(options)
                    .then(() => {
                        if (!this.keycloakAuth.authenticated) {
                            this.logger.info('[KeyCloak] logout successed');
                            observer.next(true);
                        }
                        else {
                            this.logger.info('[KeyCloak] logout failed');
                            observer.next(false);
                        }
                        // even in case of error, clear all token and authentification information
                        this.keycloakAuth.clearToken();

                        // notify 
                        this.eventStream.next('LOGGED_OUT');

                        // end of logout process ...
                        observer.complete();
                    })
                    .catch((result) => {
                        this.logger.error('[KeyCloak] errror while log out : ' + result);
                        // even in case of error, clear all token and authentification information
                        this.keycloakAuth.clearToken();

                        // notify 
                        this.eventStream.next('LOGGED_OUT');

                        observer.next(false);
                        observer.complete();
                    });
            }
            else {
                this.logger.log('No KeyCloak Active ! Not Logging out !');
                observer.next(false);
                observer.complete();
            }
        });
    }

    getToken(): Promise<string> {
        return new Promise<string>((resolve, reject) => {
            if (this.isKeyCloakActive()) {
                if (this.isLoggedIn()) {
                    this.keycloakAuth.updateToken(this.configService.config.security.keycloak.tokenMinValidity)
                        .then((refreshed) => {
                            this.checkTimeOut();
                            if (refreshed) {
                                this.logger.debug("[KeyCloak] access token refreshed !");
                                // notify 
                                this.eventStream.next('TOKEN_REFRESHED');
                            }
                            else {
                                this.logger.debug("[KeyCloak] access token still valid !");
                                // notify 
                                this.eventStream.next('TOKEN_VALIDITY_CHECKED');
                            }
                            resolve(<string>this.keycloakAuth.token);
                        })
                        .catch(() => {
                            reject('[KeyCloak] Failed to refresh access token or KeyCloak not correctaly initialized !');
                            // clear all token and authentification information
                            this.keycloakAuth.clearToken();

                            // notify 
                            this.eventStream.next('TOKEN_EXPIRED');
                        });
                }
                else {
                    reject('[KeyCloak] Not authenticated !');
                }
            }
            else {
                reject('[KeyCloak] Not active ! It is not possible to obtain or refresh a token !');
            }
        });
    }

    notifyErrorNotAuthorized(): void {
        this.eventStream.next('NOT_AUTHENTICATED');
    }

    retrieveEventStream(): Observable<string> {
        return this.eventStream.asObservable();
    }

    getInfosUser(): Promise<any> {
        // Keycloak Actif ?
        this.logger.info("getInfosUser ==> this.isKeyCloakActive() = <" + this.isKeyCloakActive() + ">");
        if (this.isKeyCloakActive()) {
            return this.waitForInitializationEnd()
                //---------------------
                .then(
                    (data) => {
                        this.logger.info("getInfosUser.waitForInitializationEnd:", data);
                        this.logger.info("getInfosUser this.isLoggedIn() ", this.isLoggedIn());
                        if (this.isLoggedIn()) {
                            return this.getToken().catch((reason) => {
                                // ce n'est pas un pb d'initialisation et on est loggue ==> seul pb possible : la session a expiree !
                                this.logger.warn('Warning - LoadInfoUser : error while refreshing token ' + reason + ' - no user info !');
                                throw 'WARN_EXPIREDTOKEN';
                            });
                        } else {
                            this.logger.info("getInfosUser not logged");
                            throw 'WARN_NOTLOGGUEDIN';
                        }
                    })
                .then(
                    (token) => {
                        this.logger.info("getInfosUser.getToken:", token);
                        if (token) {
                            return new Promise<any>((resolve, reject) => {
                                this.keycloakAuth.loadUserInfo()
                                    .then((response) => {
                                        if (response != null && undefined != response) {
                                            this.logger.debug("loadUserInfo response: ", response);
                                            this._userInfo = response as string[];
                                        }
                                        if (this.keycloakAuth.realmAccess) {
                                            this.roles = this.keycloakAuth.realmAccess['roles'];
                                            this.rolesObservable.next(this.roles);
                                        }
                                        resolve(response);
                                    })
                                    .catch((param) => {
                                        reject('Error lors du lod user info ' + param);
                                    });
                            });
                        }
                        else {
                            throw 'ERROR_BADTOKEN';
                        }
                    })
                .catch((reason) => {
                    if (reason == 'WARN_EXPIREDTOKEN') {
                        return <any>{};
                    }
                    else if (reason == 'WARN_NOTLOGGUEDIN') {
                        return <any>{};
                    }
                    else {
                        throw reason;
                    }
                });
        } else {
            this.logger.info("getInfosUser ==> keycloak not actif");
            return Promise.reject('ERROR - getInfosUser keycloak not Active ...');
        }
    }

    getRoles(): string[] {
        return this.roles;
    }

    get rolesObservable(): BehaviorSubject<string[]> {
        return this.rolesSubject;
    }


    checkTimeOut() {
        let t;
        if (this.checkTimeOutSub) {
            this.checkTimeOutSub.unsubscribe();
        }

        this.checkTimeOutSub = interval((1000 * 60 * this.configService.config.security.keycloak.IdleValidity) + (1000 * 60 * 2)).subscribe(() => { //ms * sec * min + Delta 2min

            if (this.isLoggedIn() && this.keycloakAuth.isTokenExpired()) {

                this.checkTimeOutSub.unsubscribe();

                this.notifService.emitModal("TOKEN_EXPIRED");

                this.modalResponseSubscription = this.notifService.modalResponseSubject$.subscribe(() => {
                    this.modalResponseSubscription.unsubscribe();
                    this.keycloakAuth.clearToken();
                    this.loginAfterExpiredToken();
                });
            } else {
                console.log("User already logged in");
            }

        });
    }

    loginAfterExpiredToken(): void {
        this.login().subscribe(loggedIn => {
            if (loggedIn) {
                console.log('logged IN ! Success !');
            } else {
                console.log('Not logged in - Login failed !');
            }
        });
    }
}
